import {
  ReactNode,
  SyntheticEvent,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import { ApolloError } from "@apollo/client";
import { get, map, omit } from "lodash-es";
import { useSnackbar } from "notistack";
import {
  FieldOption,
  FilterOption,
  Obj,
  TabOption,
} from "~/components/ui-library";
import {
  Access,
  CatalogType,
  Category,
  ContentPlatform,
  ContentSearchInput,
  ContentSearchResultItemOrder,
  ContentSearchResultItemOrderField,
  OrderDirection,
  Policy,
  QueryPackAddInput,
  SearchPolicyDocument,
  useAddQueryPackMutation,
  useDeletePolicyMutation,
  useLoadCategoriesLazyQuery,
  useLoadPlatformsLazyQuery,
  useSearchPolicyLazyQuery,
  useSearchQueryPackLazyQuery,
  useSetCustomPolicyMutation,
} from "~/operations";
import { useParams } from "~/hooks";
import { useNavigate, useSearchParams } from "react-router-dom";
import { INITIAL_PAGE_RANGE, PaginationProps } from "~/components/pagination";
import {
  BestPracticesGradientIcon,
  ComplianceGradientIcon,
  DefensiveGradientIcon,
} from "~/icons";
import { UploadFileInput } from "~/components/upload";
import { PolicyMrnToURIEncodedId } from "~/lib/mrn";
import parseBoolean from "~/lib/parseBoolean";
import updateQueryString from "~/lib/updateQueryString";
import {
  OrganizationScope,
  RegionScope,
  ScopeType,
  SpaceScope,
} from "~/hooks/useScope";

// Facets
interface Facets extends ContentSearchInput {
  query: string;
  namespace: string;
  catalogType: CatalogType;
  // accessLevel: Access
  // trustLevel: TrustLevel
  categories: string[];
  platforms: string[];
  orderBy: ContentSearchResultItemOrder;
  limit: number;
  after: string;
  assignedOnly?: boolean;
}

const defaultFacets: Facets = {
  query: "",
  namespace: "",
  catalogType: CatalogType.Policy,
  // accessLevel: Access.All,
  // trustLevel: TrustLevel.All,
  categories: [],
  platforms: [],
  orderBy: {
    direction: OrderDirection.Asc,
    field: ContentSearchResultItemOrderField["Name"],
  },
  // first: PAGE_SIZE,
  // ToDo: limit is first ?
  limit: 500,
  after: "",
  scopeMrn: "",
  contentMrns: [],
  includePublic: true,
  includePrivate: true,
  // @ts-ignore
  assignedOnly: "",
};

// State
const registriesTabOptions: TabOption[] = [
  {
    label: "Policies",
    value: CatalogType.Policy,
  },
  {
    label: "Query Packs",
    value: CatalogType.Querypack,
  },
];
const sortByOptions = [
  {
    name: "Name (asc)",
    value: OrderDirection.Asc,
  },
  {
    name: "Name (des)",
    value: OrderDirection.Desc,
  },
];

enum Filters {
  Namespace = "namespace",
  AccessLevel = "accessLevel",
  TrustLevel = "trustLevel",
  Categories = "categories",
  Platforms = "platforms",
  AssignedOnly = "assignedOnly",
}
const defaultFilterOptions: FilterOption[] = [
  {
    name: Filters.AssignedOnly,
    label: "Only Show Enabled Policies",
    options: ["none", "true", "false"],
    value: "",
    type: "select",
  },
  // {
  //   name: Filters.AccessLevel,
  //   label: "Access Level",
  //   options: Object.values(Access),
  //   value: defaultFacets.accessLevel,
  //   multiple: false,
  //   type: "select",
  // },
  // {
  //   name: Filters.TrustLevel,
  //   label: "Trust Level",
  //   options: Object.values(TrustLevel),
  //   value: defaultFacets.trustLevel,
  //   multiple: false,
  //   type: "select",
  // },
  {
    name: Filters.Categories,
    label: "Categories",
    // options will be populated from useCategoriesLazyQuery
    options: [],
    value: defaultFacets.categories,
    multiple: true,
    type: "select",
  },
  {
    name: Filters.Platforms,
    label: "Platforms",
    // options will be populated from usePlatformsLazyQuery
    options: [],
    value: defaultFacets.platforms,
    multiple: true,
    type: "select",
  },
];

type State = {
  isReady: boolean;
  isQuerypack: boolean;
};
const defaultState: State = {
  isReady: false,
  isQuerypack: false,
};

export type UseRegistriesReturnProps = {
  assignInProgress: string[];
  loading: boolean;
  refreshing: boolean;
  error: ApolloError | undefined;
  filtersLoading: boolean;
  filtersErrors: string[] | undefined;
  filterOptions: FilterOption[];
  sortByOptions: FieldOption[];
  registriesTabOptions: TabOption[];
  platforms: ContentPlatform[];
  categoryOptions: { name: string; text: string; icon: ReactNode }[];
  registries: Policy[];
  paginatedRegistries: Policy[];
  paginationProps: PaginationProps;
  noPolicies: boolean;
  facets: Facets;
  state: State;
  filters: Obj;
  onFacetsChange: (value: Partial<Facets>) => void;
  onStateChange: (value: Partial<State>) => void;
  onAddPolicyMutation: (value: UploadFileInput) => Promise<void>;
  onAddQueryPackMutation: (value: QueryPackAddInput) => Promise<any>;
  onChangeAssignHandler: () => Promise<any>;
  onDeletePolicyMutation: (
    e: SyntheticEvent,
    {
      policyMrn,
    }: {
      policyMrn: string;
    },
  ) => void;
};

export const registriesByCategoryOrder = [
  {
    name: "best-practices",
    icon: <BestPracticesGradientIcon fontSize="large" />,
  },
  { name: "security", icon: <DefensiveGradientIcon fontSize="large" /> },
  { name: "compliance", icon: <ComplianceGradientIcon fontSize="large" /> },
];

/**
 * API-Calls should happen only when `state.isReady` is `true`,
 *  as NextJS queryString not available on initialMount
 */
const useRegistries = ({
  scope,
  isQuerypack = false,
}: {
  scope: SpaceScope | OrganizationScope | RegionScope;
  isQuerypack?: boolean;
}): UseRegistriesReturnProps => {
  const [searchParams] = useSearchParams();
  const pageSizeParam = searchParams.get("pageSize");
  const pageSize = Number(pageSizeParam) || 10;
  const navigate = useNavigate();

  defaultFacets.scopeMrn = scope.mrn;
  defaultFacets.catalogType = isQuerypack
    ? CatalogType.Querypack
    : CatalogType.Policy;
  const { enqueueSnackbar } = useSnackbar();
  const { router, facetsFromQueryString } = useParams(defaultFacets);
  const [facets, setFacets] = useState<Facets>(defaultFacets);
  const [state, setState] = useState<State>(defaultState);

  const onFacetsChange = useCallback((value: Partial<Facets>) => {
    setFacets((prev) => {
      return { ...prev, ...value };
    });
  }, []);

  const onStateChange = useCallback((value: Partial<State>) => {
    setState((v) => ({ ...v, ...value }));
  }, []);

  const [
    useCategoriesQuery,
    {
      loading: categoriesLoading,
      error: categoriesError,
      data: { categories = [] } = {},
    },
  ] = useLoadCategoriesLazyQuery();

  const [
    usePlatformsQuery,
    {
      loading: platformsLoading,
      error: platformsError,
      data: { platforms = [] } = {},
    },
  ] = useLoadPlatformsLazyQuery();

  const [
    searchPoliciesQuery,
    {
      loading: policiesLoading,
      error: policiesError,
      data: policiesData,
      fetchMore: policiesFetchMore,
    },
  ] = useSearchPolicyLazyQuery({
    fetchPolicy: "cache-and-network",
  });

  const [
    searchQueryPacksQuery,
    {
      loading: queryPacksLoading,
      error: queryPacksError,
      data: queryPacksData,
      fetchMore: queryPacksFetchMore,
    },
  ] = useSearchQueryPackLazyQuery({
    fetchPolicy: "cache-and-network",
  });

  const onAddPolicyMutation: UseRegistriesReturnProps["onAddPolicyMutation"] =
    useCallback(
      async (input: UploadFileInput) => {
        const { data } = await setCustomPolicyMutation({
          variables: {
            input: [
              {
                scopeMrn: input.spaceMrn,
                dataurl: input.dataurl,
              },
            ],
          },
        });

        const isSinglePolicy = data?.setCustomPolicy?.policyMrns?.length === 1;
        const singlePolicyMrn = data?.setCustomPolicy?.policyMrns?.at(0);

        // If we are uploading a single policy, navigate to the policy details page
        if (isSinglePolicy && singlePolicyMrn) {
          const prefix =
            scope.type === ScopeType.Space ? "/space/findings" : "";
          navigate(
            `${prefix}/policies/add/${PolicyMrnToURIEncodedId(singlePolicyMrn)}?${scope.params}`,
          );
        }
      },
      [scope],
    );

  const [setCustomPolicyMutation] = useSetCustomPolicyMutation({
    refetchQueries: [
      {
        query: SearchPolicyDocument,
        variables: {
          input: {
            ...omit(facets, ["namespace"]),
            assignedOnly: parseBoolean(facets.assignedOnly),
          },
        },
      },
    ],
  });

  const onAddQueryPackMutation: UseRegistriesReturnProps["onAddQueryPackMutation"] =
    useCallback((input) => {
      return addQueryPackMutation({ variables: { input: [input] } });
    }, []);

  const [addQueryPackMutation] = useAddQueryPackMutation({
    refetchQueries: [
      {
        query: SearchPolicyDocument,
        variables: {
          input: {
            ...omit(facets, ["namespace"]),
            assignedOnly: parseBoolean(facets.assignedOnly),
          },
        },
      },
    ],
  });

  const onDeletePolicyMutation = useCallback(
    (e: SyntheticEvent, input: { policyMrn: string }) => {
      e.stopPropagation();
      deletePolicyMutation({ variables: { input } });
    },
    [],
  );

  const [deletePolicyMutation] = useDeletePolicyMutation({
    onCompleted: () => {
      enqueueSnackbar("Successfully deleted policy", { variant: "success" });
    },
    onError: (err) => {
      enqueueSnackbar("Failed to delete policy: " + err.message, {
        variant: "error",
      });
    },
    refetchQueries: [
      {
        query: SearchPolicyDocument,
        variables: {
          input: {
            ...omit(facets, ["namespace"]),
            assignedOnly: parseBoolean(facets.assignedOnly),
          },
        },
      },
    ],
  });

  useEffect(() => {
    const queryInput = {
      variables: {
        input: {
          scopeMrn: scope.mrn,
        },
      },
    };

    useCategoriesQuery(queryInput);
    usePlatformsQuery(queryInput);
  }, []);

  useEffect(() => {
    if (!facetsFromQueryString) {
      return;
    }

    onFacetsChange(facetsFromQueryString);
    onStateChange({
      isReady: true,
      isQuerypack: facetsFromQueryString.catalogType === CatalogType.Querypack,
    });
  }, [facetsFromQueryString]);

  const searchQuery = isQuerypack ? searchQueryPacksQuery : searchPoliciesQuery;

  useEffect(() => {
    if (!state.isReady) {
      return;
    }

    updateQueryString({
      router,
      defaultValues: { ...defaultFacets, pageSize: 10 },
      newValues: { ...facets, pageSize },
    });

    const isQuerypack = facets.catalogType === CatalogType.Querypack;
    onStateChange({
      isQuerypack,
    });

    // const searchQuery = isQuerypack
    //   ? searchQueryPacksQuery
    //   : searchPoliciesQuery;

    searchQuery({
      variables: {
        input: {
          ...omit(facets, ["namespace"]),
          assignedOnly: parseBoolean(facets.assignedOnly),
        },
      },
    });
  }, [facets, state.isReady, searchQuery]);

  const filterOptions = useMemo(() => {
    if (!categories?.length || !platforms?.length) {
      return defaultFilterOptions;
    }

    return defaultFilterOptions.map((filterOption) => {
      if (filterOption.name === Filters.Categories) {
        return {
          ...filterOption,
          options: map(categories, "name"),
        };
      } else if (filterOption.name === Filters.Platforms) {
        return {
          ...filterOption,
          options: map(platforms, "name"),
        };
      }

      return filterOption;
    });
  }, [categories, platforms]);

  const categoryOptions = useMemo(() => {
    if (!categories?.length) {
      return [];
    }

    return registriesByCategoryOrder.map(({ name, icon }) => {
      const category = categories.find((val) => val?.name === name) as Category;

      return {
        name: category?.name || "",
        text: state.isQuerypack
          ? `${category?.packs || 0} Query Packs`
          : `${category?.policies || 0} Policies`,
        icon,
      };
    });
  }, [categories, state.isQuerypack]);

  const fetchMore = useMemo(() => {
    return state.isQuerypack ? queryPacksFetchMore : policiesFetchMore;
  }, [state.isQuerypack]);

  const registries = useMemo(() => {
    const edges = state.isQuerypack
      ? queryPacksData?.content?.edges
      : policiesData?.content?.edges;
    const nodes = edges
      ? edges
          .map(({ node }) => node as Policy)
          .filter((node) => node.mrn)
          .map((node) => ({
            ...node,
            access: Access.All,
            namespace:
              node.trustLevel === "PRIVATE"
                ? scope.id
                : get(node, "namespace") || "mondoohq",
            githubUrl: node.githubUrl || "",
            uid: (node.uid || node.mrn.split("/").pop()) as string,
          }))
      : [];

    return nodes;
  }, [
    queryPacksData?.content?.edges,
    policiesData?.content?.edges,
    state.isQuerypack,
  ]);

  const [pageItems, setPageItems] = useState(INITIAL_PAGE_RANGE);

  const paginatedRegistries = useMemo(() => {
    return registries.slice(pageItems.from, pageItems.to);
  }, [pageItems.from, pageItems.to, registries]);

  const filters = useMemo(() => {
    return {
      // namespace: facets.namespace,
      // accessLevel: facets.accessLevel,
      // trustLevel: facets.trustLevel,
      categories: facets.categories,
      platforms: facets.platforms,
      assignedOnly: facets.assignedOnly,
    };
  }, [
    // facets.namespace,
    // facets.accessLevel,
    // facets.trustLevel,
    facets.categories,
    facets.platforms,
    facets.assignedOnly,
  ]);

  const onChangeAssignHandler = useCallback(async () => {
    await searchQuery({
      variables: {
        input: {
          ...omit(facets, ["namespace"]),
          assignedOnly: parseBoolean(facets.assignedOnly),
        },
      },
    });
  }, [searchQuery, facets]);

  const paginationProps: PaginationProps = {
    totalCount: registries.length,
    setPageItems,
    fetchMore,
    sx: { borderBottomLeftRadius: 4, borderBottomRightRadius: 4 },
  };

  return {
    loading:
      paginatedRegistries.length > 0
        ? false
        : state.isQuerypack
          ? queryPacksLoading
          : policiesLoading,
    refreshing:
      paginatedRegistries.length > 0
        ? queryPacksLoading || policiesLoading
        : false,
    assignInProgress: [],
    error: state.isQuerypack ? queryPacksError : policiesError,
    noPolicies: Boolean(paginatedRegistries?.length === 0),
    filtersLoading: categoriesLoading || platformsLoading,
    filtersErrors: [categoriesError, platformsError]
      .filter(Boolean)
      .map(String),
    filterOptions,
    registriesTabOptions,
    platforms: platforms as ContentPlatform[],
    categoryOptions,
    registries,
    sortByOptions,
    paginatedRegistries,
    paginationProps,
    facets,
    state,
    filters,
    onFacetsChange,
    onStateChange,
    onAddPolicyMutation,
    onAddQueryPackMutation,
    onChangeAssignHandler,
    onDeletePolicyMutation,
  };
};

export default useRegistries;
